/**
 * Provides a context for managing state and selectors related to VMS Control components
 * @module WeatherForecastContext
 */
import { selectIssuedBy, selectTextDescription, selectWeatherForecasts } from '@awp/stores/dataVisualizationSlice';
import React, { createContext, useContext, useRef, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getBreakpoints } from '@awp/helpers/UIHelper';
import { debounce } from '@awp/helpers/Utils';

/**
 * Context for managing state and selectors related to VMS control components
 * @type {React.Context}
 */
const WeatherForecastContext = createContext();

/**
 * Provider component for WeatherForecastContext.
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - Child components wrapped by the provider.
 * @returns {React.ReactNode} Provider component.
 */
export const WeatherForecastContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const allWeatherForecasts = useSelector(selectWeatherForecasts);

  const [issuedBy, setIssuedBy] = useState(null);
  const [forecastId, setForecastId] = useState(null);
  const [forecaster, setForecaster] = useState(null);
  const [confidence, setConfidence] = useState(null);
  const [hourlyStart, setHourlyStart] = useState(null);
  const [hourlyEnd, setHourlyEnd] = useState(null);
  const [breakpoints, setBreakpoints] = useState(getBreakpoints());
  const textDescription = useSelector(selectTextDescription);

  const issuedByOptions = [{ value: 'Weathernet', label: 'Weathernet' }];

  useEffect(() => {
    setIssuedBy(issuedByOptions[0].value);
  }, []);
  useEffect(() => {
    const handleResize = debounce(() => {
      setBreakpoints(getBreakpoints());
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });
  useMemo(() => {
    if (allWeatherForecasts.length === 0) {
      setForecastId(null);
    } else {
      if (allWeatherForecasts[0].Id !== forecastId) {
        setForecastId(allWeatherForecasts[0].Id);
      } else {
        setForecastId(allWeatherForecasts.find((f) => f.Id == forecastId)?.Id || allWeatherForecasts[0].Id);
      }
    }
  }, [allWeatherForecasts]);

  useMemo(() => {
    if (textDescription.Id !== forecastId) {
      setConfidence(null);
      setForecaster(null);
      setHourlyStart(null);
      setHourlyEnd(null);
    } else {
      setConfidence(textDescription.Confidence);
      setForecaster(textDescription.Forecaster);
      setHourlyStart(textDescription.HourlyStart);
      setHourlyEnd(textDescription.HourlyEnd);
    }
  }, [textDescription, forecastId]);

  // Structure the context values for the provider
  const stateValues = { issuedBy, forecastId, forecaster, confidence, hourlyStart, hourlyEnd, breakpoints };

  const stateSetters = {
    setIssuedBy,
    setForecastId,
    setForecaster,
    setConfidence,
    setHourlyStart,
    setHourlyEnd,
    setBreakpoints,
  };

  const selectorValues = { allWeatherForecasts, textDescription };

  const memoizedValues = {};

  // Combine values into a single context value
  const contextValue = {
    ...stateValues,
    ...stateSetters,
    ...selectorValues,
    ...memoizedValues,
    issuedByOptions,
  };

  return <WeatherForecastContext.Provider value={contextValue}>{children}</WeatherForecastContext.Provider>;
};

/**
 * Hook for consuming WeatherForecastContext.
 * @returns {object} Context value.
 */
export const useWeatherForecastContext = () => useContext(WeatherForecastContext);
